import { Link } from "@/components";
import { PageWrapper } from "@/components/PageWrapper";
import { CONTENT_WIDTH, GUTTER_SIZE } from "@/enums";
import { routes } from "@/helpers";
import {
  useGetApprovalWorkflowQuery,
  useGetProjectRolesQuery,
  useSaveApprovalWorkflowMutation,
} from "@/services";
import { Button, Card, Col, Form, Row, Select, Spin } from "antd";
import _ from "lodash";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { flattenApprovers, updateDependentApprovers } from "../helpers";

const AddWorkflowApprover = () => {
  const intl = useIntl();
  const { id, approverId } = useParams();

  const [loading, setLoading] = useState(true);
  const [optionsLoaded, setOptionsLoaded] = useState(false);
  const [approver, setApprover] = useState();
  const [templateRolesOptions, setTemplateRolesOptions] = useState();
  const [dependentName, setDependentName] = useState();
  const navigate = useNavigate();

  const [saveApprovalWorkflow] = useSaveApprovalWorkflowMutation();
  const { refetch, data: approvalWorkflow } = useGetApprovalWorkflowQuery(id);
  const { data: projectRoles } = useGetProjectRolesQuery();

  useEffect(() => {
    const filterTemplateRoles = (approvers) => {
      let flattenedApprovers = [];
      if (approvers) {
        flattenedApprovers = flattenApprovers(approvers);
        const parentApprover = flattenedApprovers.find(
          ({ id }) => id === approverId
        );
        setDependentName(parentApprover?.name);
      }
      if (projectRoles?.length) {
        const filteredTemplateRoles = projectRoles.filter(
          ({ id }) =>
            !flattenedApprovers.some(({ secondaryId }) => secondaryId === id)
        );
        convertTemplateRolesOptions(filteredTemplateRoles);
      }
      if (templateRolesOptions?.length) {
        setOptionsLoaded(true);
      }
    };

    if (optionsLoaded) return;

    filterTemplateRoles(approvalWorkflow?.approvers);
  }, [
    approvalWorkflow?.approvers,
    approverId,
    optionsLoaded,
    projectRoles,
    templateRolesOptions?.length,
  ]);

  useEffect(() => {
    setLoading(false);
  }, [templateRolesOptions]);

  const handleSaveButtonClick = () => {
    const dependentApprover = {
      secondaryId: approver,
      name: projectRoles.find(({ id }) => id === approver).name,
    };
    const updatedApprovalWorkFlow = _.cloneDeep(approvalWorkflow);
    updateDependentApprovers(
      approverId,
      updatedApprovalWorkFlow,
      dependentApprover
    );
    saveApprovalWorkflow(updatedApprovalWorkFlow)
      .unwrap()
      .then(() => {
        toast.success(intl.formatMessage({ id: "general.toastChangesSaved" }));
        refetch();
        navigate(
          routes.admin.approvalWorkFlows.get(updatedApprovalWorkFlow.id)
        );
      })
      .catch(() =>
        toast.error(
          intl.formatMessage({ id: "general.toastSomethingWentWrong" })
        )
      );
  };

  const convertTemplateRolesOptions = (templateRoles) => {
    const options = [];
    templateRoles.forEach(({ name, id }) => {
      options.push({ label: name, value: id });
    });
    setTemplateRolesOptions(options);
  };

  const ApproverBreadCrumb = () => (
    <>
      {approverId &&
        `${intl.formatMessage({
          id: "approvalWorkFlows.addDependentApproverLabel",
        })} - ${dependentName}`}
      {!approverId &&
        intl.formatMessage({
          id: "approvalWorkFlows.addIndependentApproverLabel",
        })}
    </>
  );

  return (
    <PageWrapper
      pageTitle={intl.formatMessage({ id: "approvalWorkFlows.editTitle" })}
      backButtonRoute={routes.admin.approvalWorkFlows.get(id)}
    >
      {loading ? (
        <Spin />
      ) : (
        <Card>
          <Form>
            <Row gutter={GUTTER_SIZE.large}>
              <Col span={CONTENT_WIDTH.half}>
                <h2>
                  {!!approvalWorkflow && (
                    <Link
                      data-test-id="add-work-flow-approver-name-link"
                      to={routes.admin.approvalWorkFlows.get(
                        approvalWorkflow.id
                      )}
                    >
                      {approvalWorkflow.name}
                    </Link>
                  )}{" "}
                  {" / "}
                  <ApproverBreadCrumb />
                </h2>
                <p>
                  {intl.formatMessage({
                    id: "approvalWorkFlows.approverSubTitle",
                  })}
                </p>
                <div>
                  <p data-test-id="add-work-flow-approver-select-label">
                    {intl.formatMessage({
                      id: "approvalWorkFlows.approverLabel",
                    })}
                  </p>
                  <Select
                    data-test-id="add-work-flow-approver-select"
                    placeholder={intl.formatMessage({
                      id: "approvalWorkFlows.selectApproverPlaceholder",
                    })}
                    value={approver}
                    onChange={(value) => setApprover(value)}
                    options={templateRolesOptions}
                    filterOption={(input, option) => {
                      if (!input?.length) return true;
                      if (!option.label) return false;
                      return (
                        option.label
                          .toLowerCase()
                          .indexOf(input.toLowerCase()) >= 0
                      );
                    }}
                    showSearch={true}
                  />
                </div>
                <Button
                  data-test-id="add-work-flow-approver-add-button"
                  type="primary"
                  size="large"
                  disabled={!approver}
                  style={{ float: "right" }}
                  onClick={handleSaveButtonClick}
                >
                  {intl.formatMessage({
                    id: "general.add",
                  })}
                </Button>
                <Button
                  data-test-id="add-work-flow-approver-cancel-button"
                  size="large"
                  style={{ float: "right", marginRight: "20px" }}
                  onClick={() => {
                    navigate(
                      routes.admin.approvalWorkFlows.get(approvalWorkflow.id)
                    );
                  }}
                >
                  {intl.formatMessage({
                    id: "general.cancel",
                  })}
                </Button>
              </Col>
            </Row>
          </Form>
        </Card>
      )}
    </PageWrapper>
  );
};

export default AddWorkflowApprover;
